/**
 * EncounterDocumentationPanel Component
 *
 * Renders the org's configured telehealth progress-note FORM (via FormRenderer,
 * from the encounter's linked form template) plus an encounter-type selector and
 * Save/Sign. Field values map to the encounter's documentation columns so the AI
 * transcription "Apply" and the existing lifecycle keep working; on Sign the
 * linked form_instance is submitted to the member record.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, FileText, AlertCircle, Loader2, PenLine, Clock } from 'lucide-react';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { telehealthEncounterAPI } from '@/services/api/telehealth';
import { FormRenderer } from '@/components/forms/FormRenderer';
import { EncounterDocumentationPanelProps, EncounterDocumentation, ENCOUNTER_STATUS_LABELS } from './types';

// Encounter form field_id -> encounter documentation column.
const FIELD_TO_DOC: Record<string, keyof EncounterDocumentation> = {
  chief_complaint: 'chief_complaint',
  reason_for_visit: 'reason_for_visit',
  subjective: 'subjective_notes',
  objective: 'objective_notes',
  assessment: 'assessment_notes',
  plan: 'plan_notes',
  member_goals_discussed: 'member_goals_discussed',
  barriers_identified: 'barriers_identified',
  interventions_provided: 'interventions_provided',
  resources_provided: 'resources_provided',
  sdoh_domains_addressed: 'sdoh_domains_addressed',
};

const ENCOUNTER_TYPES: Array<{ value: string; label: string }> = [
  { value: 'telehealth_visit', label: 'Telehealth visit' },
  { value: 'care_coordination', label: 'Care coordination' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'crisis_intervention', label: 'Crisis intervention' },
];

function initValues(encounter: any): Record<string, any> {
  return {
    chief_complaint: encounter.chief_complaint || '',
    reason_for_visit: encounter.reason_for_visit || '',
    subjective: encounter.subjective_notes || '',
    objective: encounter.objective_notes || '',
    assessment: encounter.assessment_notes || '',
    plan: encounter.plan_notes || '',
    member_goals_discussed: encounter.member_goals_discussed || '',
    barriers_identified: encounter.barriers_identified || '',
    interventions_provided: encounter.interventions_provided || '',
    resources_provided: encounter.resources_provided || '',
    sdoh_domains_addressed: encounter.sdoh_domains_addressed || [],
  };
}

export default function EncounterDocumentationPanel({
  encounter,
  isEditing,
  onUpdate,
  onSave,
  onSign,
  className = '',
}: EncounterDocumentationPanelProps) {
  const queryClient = useQueryClient();
  const { fmt } = useFormattedDate();

  const [sections, setSections] = useState<any[]>([]);
  const [values, setValues] = useState<Record<string, any>>(initValues(encounter));
  const [encounterType, setEncounterType] = useState<string>(encounter.encounter_type || 'telehealth_visit');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  const isReadOnly = !isEditing || encounter.status === 'signed' || encounter.status === 'locked';

  // Ensure the encounter has an org form template + instance; render its sections.
  const { data: formData } = useQuery({
    queryKey: ['telehealth-encounter-form', encounter.id],
    queryFn: () => telehealthEncounterAPI.ensureEncounterForm(encounter.id),
  });

  useEffect(() => {
    if (formData?.template?.sections?.length) setSections(formData.template.sections);
    // Merge any AI-applied field_data from the instance into the visible values.
    if (formData?.instance?.field_data) {
      setValues((prev) => ({ ...prev, ...formData.instance!.field_data }));
    }
  }, [formData]);

  useEffect(() => {
    setValues(initValues(encounter));
    setEncounterType(encounter.encounter_type || 'telehealth_visit');
  }, [encounter]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => telehealthEncounterAPI.updateEncounter(encounter.id, data),
    onSuccess: () => {
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      queryClient.invalidateQueries({ queryKey: ['telehealth-encounter', encounter.id] });
    },
  });

  const signMutation = useMutation({
    mutationFn: () => telehealthEncounterAPI.signEncounter(encounter.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['telehealth-encounter', encounter.id] });
      onSign?.();
    },
  });

  const onFieldChange = useCallback(
    (fieldId: string, value: any) => {
      setValues((prev) => ({ ...prev, [fieldId]: value }));
      setHasUnsavedChanges(true);
      const col = FIELD_TO_DOC[fieldId];
      if (col) onUpdate({ [col]: value } as Partial<EncounterDocumentation>);
    },
    [onUpdate]
  );

  const buildDocumentation = (): EncounterDocumentation => {
    const doc: any = {};
    Object.entries(values).forEach(([fieldId, v]) => {
      const col = FIELD_TO_DOC[fieldId] || fieldId;
      doc[col] = v;
    });
    return doc as EncounterDocumentation;
  };

  const handleTypeChange = async (value: string) => {
    setEncounterType(value);
    try {
      await updateMutation.mutateAsync({ encounter_type: value });
    } catch {
      /* non-fatal */
    }
  };

  const handleSave = async () => {
    await updateMutation.mutateAsync({ documentation: buildDocumentation(), status: 'in_progress' });
    onSave?.();
  };

  const handleSign = async () => {
    if (hasUnsavedChanges) await handleSave();
    await signMutation.mutateAsync();
  };

  return (
    <div className={`h-full flex flex-col bg-white dark:bg-gray-900 ${className}`}>
      {/* Header */}
      <div className="p-4 border-b bg-white dark:bg-gray-900 sticky top-0 z-10 space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-900 dark:text-gray-100">
            <FileText className="h-5 w-5 text-teal-600" />
            Encounter Documentation
          </h2>
          <div className="flex items-center gap-2">
            {hasUnsavedChanges && (
              <span className="text-xs text-amber-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> Unsaved
              </span>
            )}
            {lastSaved && (
              <span className="text-xs text-gray-500 flex items-center gap-1">
                <Clock className="h-3 w-3" /> Saved {fmt.time(lastSaved)}
              </span>
            )}
          </div>
        </div>

        {/* Encounter type + status */}
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-gray-500">Type</label>
          <select
            value={encounterType}
            disabled={isReadOnly}
            onChange={(e) => handleTypeChange(e.target.value)}
            className="border rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 dark:border-gray-700"
          >
            {ENCOUNTER_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <span className="text-xs text-gray-500 ml-auto">
            Status: <span className="font-medium">{ENCOUNTER_STATUS_LABELS[encounter.status] || encounter.status}</span>
          </span>
        </div>

        {formData?.template?.name && (
          <div className="text-xs text-gray-400">Documenting into: {formData.template.name}</div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2">
          {!isReadOnly && (
            <button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
            >
              {updateMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </button>
          )}
          {encounter.status !== 'signed' && encounter.status !== 'locked' && (
            <button
              onClick={handleSign}
              disabled={signMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              {signMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />}
              Sign
            </button>
          )}
        </div>
      </div>

      {/* Form (org template) */}
      <div className="flex-1 overflow-y-auto p-4">
        {sections.length > 0 ? (
          <FormRenderer sections={sections} value={values} onChange={onFieldChange} disabled={isReadOnly} />
        ) : (
          <div className="text-sm text-gray-500 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading encounter form…
          </div>
        )}
      </div>
    </div>
  );
}
